/**
 * The workspace switcher's behaviour: which workspaces this person has, which one THIS TAB is in, and switching,
 * setting the personal workspace aside, and starting a new organization.
 *
 * Lifted out of src/components/WorkspacePill.tsx (design-system §7b rule 1) so today's pill and the new design's app
 * header run the same rules; the pill keeps only its markup and its open/close. The rules, unchanged:
 *
 * - THE LIST (speed round, 2026-08-16). The persisted, server-verified copy paints at once; the callable revalidates
 *   in the background; `ensureNativeOrg` runs in parallel with a forced refresh after it, so a first-ever sign-in
 *   converges on the freshly minted native org within one extra fetch. Any failure lists nothing (the chrome must
 *   never break over workspace listing).
 * - LIVE NAMES (2026-08-17). Renames land on users/{uid}/workspace_index server-side and repaint in real time; a
 *   name from the index overrides the list's label (labels only — membership and switching stay with the verified
 *   callables), and the shared cache is patched.
 * - NEVER A GUESS (INCIDENT-20260818; per tab, 2026-09-11). This tab's own pin first, then this device's confirmed
 *   workspace, then the list's account-wide `isActive`; with none of them, a neutral resolving state.
 * - THE SWITCH (sealed, 2026-08-19/20). The transition controller raises the blocking overlay, runs the
 *   server-verified switch beneath it, writes every arrival marker and hard-reloads in place. A dormant row rides the
 *   same path (the controller activates it and lands on /onboarding). A row already current HERE is a no-op.
 * - SET ASIDE (2026-09-09). Returns the personal workspace to its "set up" row; deletes nothing. The server decides
 *   whether it is offered (`canSetAside`). In a fleet not in lazy mode the platform would re-activate it, so it says
 *   so rather than pretending.
 * - A NEW ORGANIZATION (task #75, 2026-08-18). The staged onboarding runs for it.
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { ensureNativeOrg, type WorkspaceOption } from '../services/rbacService';
import { beginWorkspaceTransition, subscribeWorkspaceTransition } from '../services/workspaceTransition';
import {
  clearWorkspaceOptionsCache,
  fetchWorkspaceOptionsCached,
  patchCachedDisplayNames,
  revalidateWorkspaceOptions,
} from '../services/workspaceOptionsCache';
import { readTabWorkspacePin } from '../services/prest0nRuntime/tabWorkspace';
import { readConfirmedWorkspace } from '../services/prest0nRuntime/confirmedWorkspaceCache';
import { subscribeWorkspaceNames } from '../services/workspaceIndexLive';
import { workspaceMarkColor, workspaceLabel } from '../services/workspaceIdentity';
import { WORKSPACE_PILL_WORDS as W } from '../components/workspacePillWords';

/** One workspace as the switcher shows it. */
export interface WorkspaceRow {
  option: WorkspaceOption;
  accountId: string;
  /** Its name as this person sees it (never a raw account id). */
  label: string;
  /** Its colour mark, distinct from every other workspace on the roster. */
  markColor: string;
  /** Current IN THIS TAB (the row the pill's label names). */
  current: boolean;
  /** This person owns it. */
  yours: boolean;
  /** The words after the name: switching, current, set up, a role, or nothing for one they own. */
  hint: string;
}

export interface WorkspaceSwitcher {
  /** Signed in with at least one workspace listed: the switcher shows. */
  ready: boolean;
  rows: WorkspaceRow[];
  /** The workspace this tab is in, or null while it resolves. */
  active: WorkspaceRow | null;
  /** The personal workspace the server lets this person set aside, if any. */
  setAsideRow: WorkspaceRow | null;
  /** A switch is under way (every row waits). */
  switching: boolean;
  settingAside: boolean;
  switchTo: (row: WorkspaceRow) => void;
  /** 'eager-mode': the platform would re-activate it, nothing changed (close the menu). Otherwise the page leaves. */
  setAside: (row: WorkspaceRow) => Promise<'eager-mode' | 'leaving' | 'failed' | 'busy'>;
  startNewOrganization: () => void;
}

export function useWorkspaceSwitcher(): WorkspaceSwitcher {
  const { isAuthenticated, user } = useAuth();
  const [workspaces, setWorkspaces] = useState<WorkspaceOption[]>([]);
  const [liveNames, setLiveNames] = useState<Record<string, string>>({});
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const [settingAside, setSettingAside] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    let alive = true;
    const persistUid = user?.id || null;
    fetchWorkspaceOptionsCached(persistUid)
      .then((options) => {
        if (alive && options.length > 0) setWorkspaces(options);
      })
      .catch(() => {
        if (alive) setWorkspaces([]);
      });
    ensureNativeOrg()
      .then(() => revalidateWorkspaceOptions(persistUid))
      .then((options) => {
        if (alive && options.length > 0) setWorkspaces(options);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    const uid = user.id;
    return subscribeWorkspaceNames(uid, (names) => {
      setLiveNames(names);
      patchCachedDisplayNames(uid, names);
    });
  }, [isAuthenticated, user?.id]);

  // A failed sealed switch releases the overlay: the rows come back (success never returns: the page reloads).
  useEffect(() => subscribeWorkspaceTransition((s) => {
    if (!s) setSwitchingId(null);
  }), []);

  const uid = user?.id;
  const ready = isAuthenticated && workspaces.length > 0;
  const displayed = workspaces.map((w) =>
    liveNames[w.accountId] ? { ...w, displayName: liveNames[w.accountId] } : w,
  );
  const tabPin = readTabWorkspacePin();
  const confirmed = readConfirmedWorkspace(uid);
  const activeAccountId =
    (tabPin && (!tabPin.uid || !uid || tabPin.uid === uid) ? tabPin.accountId : null)
    ?? confirmed?.accountId
    ?? null;
  const activeOption =
    (activeAccountId ? displayed.find((w) => w.accountId === activeAccountId) : undefined)
    ?? displayed.find((w) => w.isActive)
    ?? null;
  // The full roster feeds the colour marks, so every dot on screen stays distinct.
  const rosterIds = workspaces.map((w) => w.accountId);

  const toRow = (ws: WorkspaceOption): WorkspaceRow => {
    const current = activeOption ? ws.accountId === activeOption.accountId : Boolean(ws.isActive);
    return {
      option: ws,
      accountId: ws.accountId,
      label: workspaceLabel(ws, uid),
      markColor: workspaceMarkColor(ws.accountId, rosterIds),
      current,
      yours: Boolean(ws.isOwner),
      hint: switchingId === ws.accountId
        ? (ws.dormant ? W.settingUp : W.switching)
        : current
          ? W.current
          : ws.dormant
            ? W.setUp
            : ws.isOwner
              ? ''
              : ws.roleLabel || W.member,
    };
  };
  const rows = ready ? displayed.map(toRow) : [];
  const active = ready && activeOption ? toRow(activeOption) : null;
  const setAsideOption = ready ? displayed.find((w) => w.canSetAside) : undefined;
  const setAsideRow = setAsideOption ? toRow(setAsideOption) : null;

  const switchTo = (row: WorkspaceRow) => {
    if (row.current || switchingId) return;
    setSwitchingId(row.accountId);
    beginWorkspaceTransition({
      targetAccountId: row.accountId,
      targetLabel: workspaceLabel(row.option, uid),
      uid,
      navigation: 'reload',
    });
  };

  const setAside = async (): Promise<'eager-mode' | 'leaving' | 'failed' | 'busy'> => {
    if (settingAside || switchingId) return 'busy';
    setSettingAside(true);
    try {
      const { deactivatePersonalWorkspace } = await import('../services/rbacService');
      const res = await deactivatePersonalWorkspace();
      if (!res.deactivated && res.reason === 'eager-mode') {
        setSettingAside(false);
        return 'eager-mode';
      }
      try { clearWorkspaceOptionsCache(); } catch { /* revalidates on load */ }
      // Hard navigation: module-level accountId caches re-key only on a fresh load. /onboarding is where the
      // personal-vs-company choice lives.
      window.location.assign('/onboarding');
      return 'leaving';
    } catch {
      setSettingAside(false);
      return 'failed';
    }
  };

  const startNewOrganization = () => {
    window.location.assign('/onboarding?neworg=1');
  };

  return {
    ready,
    rows,
    active,
    setAsideRow,
    switching: Boolean(switchingId),
    settingAside,
    switchTo,
    setAside,
    startNewOrganization,
  };
}
